<template>
  <!-- Schreibbereich beim Antworten in einer Unterhaltung – gemeinsam für das
       Antwortfeld der Korrespondenz und das schwebende Unterhaltungs-Fenster.
       Editor (MessageComposer) und Werkzeugleiste (ComposeTools) sind
       dieselben wie in „Neue Nachricht“ (Kundenfeedback 25.10.2026 Teil 3,
       I); hier kommt nur die Vorbelegung (Signatur, Zitat) und die
       Anhangsliste der Antwort hinzu. -->
  <div class="rc" :class="{ 'rc--active': activated }">
    <MessageComposer
      ref="composer"
      class="rc__composer"
      :model-value="modelValue"
      :placeholder="placeholder"
      :toolbar="activated"
      @focus="activate"
      @send="emit('send')"
      @update:model-value="emit('update:modelValue', $event)"
    />

    <div v-if="files.length > 0 || documents.length > 0" class="rc__files" data-testid="reply-attachments">
      <span v-for="(f, i) in files" :key="`${f.name}-${i}`" class="rc__file">
        <Paperclip :size="12" />{{ f.name }}
        <button
          :aria-label="$gt('Entfernen')"
          class="rc__file-x"
          :title="$gt('Entfernen')"
          type="button"
          @click="removeFile(i)"
        ><X :size="12" /></button>
      </span>
      <span v-for="(d, i) in documents" :key="`doc-${d.idCase}-${d.idDocument}`" class="rc__file" :title="$gt('Dokument aus der Akte')">
        <FolderOpen :size="12" />{{ d.name }}
        <button
          :aria-label="$gt('Entfernen')"
          class="rc__file-x"
          :title="$gt('Entfernen')"
          type="button"
          @click="removeDocument(i)"
        ><X :size="12" /></button>
      </span>
    </div>

    <ComposeTools
      :case-type="caseData?.caseType || ''"
      class="rc__tools"
      :composer="composerHandle"
      :html="modelValue"
      :picker-case="pickerCase"
      :prepare="loadCaseData"
      :signature="signature"
      :subject="conversation?.subject || ''"
      :suggest-category="suggestCategory"
      :template-values="templateValues"
      @attach-case="onPickerAttach"
      @attach-files="onFiles"
      @changed="activate"
    >
      <template #end>
        <slot name="actions" />
      </template>
    </ComposeTools>
  </div>
</template>

<script setup lang="ts">
  import type { CaseDocumentRef } from '../chat/chatApi'
  import type { ChatConversation } from '../chat/chatModel'
  import type { AttachPayload } from './CaseAttachmentPickerDialog.vue'
  import type { ComposerHandle } from './ComposeTools.vue'
  import { FolderOpen, Paperclip, X } from 'lucide-vue-next'
  import { computed, nextTick, ref, watch } from 'vue'
  import MessageComposer from '@/components/messages/MessageComposer.vue'
  import { useInsuranceDirectory } from '@/composables/useInsuranceDirectory'
  import { gt } from '@/i18n/gt'
  import { useSnackbarStore } from '@/stores/snackbarStore'
  import { replyQuoteHtml } from '../chat/composePrefill'
  import { hasSignature, insertQuotedHistory, ownText, setSignature } from './composeBody'
  import { loadCaseComposeResources, loadCaseInvolved, loadCaseType } from './composeCaseData'
  import { type ComposeCaseData, composeTemplateValues, suggestTemplateCategory } from './composeTemplate'
  import ComposeTools from './ComposeTools.vue'
  import { useComposeSignature } from './useComposeSignature'

  const props = withDefaults(defineProps<{
    /** Unterhaltung, in die geantwortet wird (Akte, Betreff, letzte Nachricht). */
    conversation: ChatConversation | null
    modelValue: string
    files: File[]
    documents: CaseDocumentRef[]
    placeholder?: string
    /** Beim ersten Schreiben die beantwortete Nachricht zitieren. */
    quote?: boolean
  }>(), { placeholder: '', quote: true })

  const emit = defineEmits<{
    'update:modelValue': [html: string]
    'update:files': [files: File[]]
    'update:documents': [documents: CaseDocumentRef[]]
    'send': []
  }>()

  const composer = ref<InstanceType<typeof MessageComposer> | null>(null)
  const signature = useComposeSignature()
  const activated = ref(false)
  const caseData = ref<ComposeCaseData | null>(null)
  let caseDataFor: number | null = null

  const pickerCase = computed(() => (props.conversation?.idCase
    ? { id: props.conversation.idCase, caseNumber: props.conversation.caseNumber || '', caseDescription: props.conversation.caseDescription || '' }
    : null))
  const templateValues = computed(() => composeTemplateValues(signature.senderInfo(), caseData.value
    ?? (props.conversation?.caseNumber ? { caseNumber: props.conversation.caseNumber } : null)))

  function composerHandle (): ComposerHandle | null {
    return (composer.value as unknown as ComposerHandle | null) ?? null
  }

  function editorEl (): HTMLElement | null {
    return composerHandle()?.editorEl() ?? null
  }

  // Neue Unterhaltung: Falldaten gehören zur bisherigen Akte.
  watch(() => props.conversation?.idCase ?? null, idCase => {
    if (idCase !== caseDataFor) {
      caseData.value = null
      caseDataFor = null
    }
  })

  /**
   * Falldaten für Vorlagen-Platzhalter – dieselben Abrufe wie in „Neue
   * Nachricht“, damit Platzhalter beim Antworten genauso befüllt werden.
   */
  async function loadCaseData (): Promise<void> {
    await signature.ensureSignatureLoaded()
    const conv = props.conversation
    if (!conv?.idCase || caseDataFor === conv.idCase) {
      return
    }
    const idCase = conv.idCase
    const [resources, involved, caseType] = await Promise.all([
      loadCaseComposeResources(idCase),
      loadCaseInvolved(idCase),
      loadCaseType(idCase),
    ])
    if (props.conversation?.idCase !== idCase) {
      return
    }
    caseDataFor = idCase
    caseData.value = {
      caseNumber: conv.caseNumber,
      // Kasko-Akten: Kasko-Schreiben zuerst, Haftpflicht-Vorlagen entfallen.
      caseType,
      clients: resources.clients,
      vehicles: resources.vehicles,
      opponents: resources.opponents,
      accidents: resources.accidents,
      involved,
    }
  }

  /** Vorauswahl „An wen?“: die erste externe Adresse der Unterhaltung. */
  function suggestCategory (): string {
    const participants = (props.conversation?.participants || []).filter(p => p.externalEmail)
    const first = participants.find(p => p.role === 'to') || participants[0]
    if (!first) {
      return ''
    }
    try {
      const directory = useInsuranceDirectory()
      return suggestTemplateCategory(first.externalEmail, caseData.value, name => directory.matchInsurance(name))
    } catch {
      return ''
    }
  }

  function placeCaretAtStart (el: HTMLElement) {
    const range = document.createRange()
    range.setStart(el, 0)
    range.collapse(true)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  /**
   * Beim ersten Schreiben vorbelegen wie „Neue Nachricht“: Signatur des
   * gewählten Standorts, darunter das Zitat der beantworteten Nachricht.
   */
  async function activate (): Promise<void> {
    if (activated.value) {
      return
    }
    activated.value = true
    await signature.ensureSignatureLoaded()
    const el = editorEl()
    if (!el) {
      return
    }
    const base = el.innerHTML
    let html = base
    if (!hasSignature(base)) {
      const sig = signature.getRenderedSignature()
      if (sig) {
        html = setSignature(html, sig)
      }
    }
    if (props.quote) {
      html = insertQuotedHistory(html, replyQuoteHtml(props.conversation?.lastMessage))
    }
    if (html === base) {
      return
    }
    const hadText = ownText(base).length > 0
    el.innerHTML = html
    composerHandle()?.syncFromEditor()
    if (!hadText) {
      placeCaretAtStart(el)
    }
  }

  function onFiles (picked: File[]) {
    emit('update:files', [...props.files, ...picked])
    void activate()
  }

  function removeFile (index: number) {
    emit('update:files', props.files.filter((_, i) => i !== index))
  }

  function removeDocument (index: number) {
    emit('update:documents', props.documents.filter((_, i) => i !== index))
  }

  /** Auswahl aus „Aus Fallakte“: Dokumente als Referenz, E-Mails als PDF-Datei. */
  function onPickerAttach (payload: AttachPayload) {
    if (payload.documents.some(d => d.doc.locked)) {
      useSnackbarStore().showError(gt('Gesperrte Dokumente werden nicht angehängt.'))
    }
    const fresh = payload.documents
      .filter(d => !d.doc.locked)
      .filter(d => !props.documents.some(x => x.idCase === d.idCase && x.idDocument === d.doc.id))
      .map(d => ({ idCase: d.idCase, idDocument: d.doc.id, name: String(d.doc.title || d.doc.filename || gt('Dokument')) }))
    emit('update:documents', [...props.documents, ...fresh])
    if (payload.files.length > 0) {
      emit('update:files', [...props.files, ...payload.files])
    }
    void activate()
  }

  /** Zurück in den Ausgangszustand (nach Senden/Verwerfen, Zielwechsel ohne Entwurf). */
  function reset () {
    activated.value = false
  }

  /** Entwurf wiederhergestellt: nicht erneut vorbelegen. */
  function markActive () {
    activated.value = true
  }

  async function focus (): Promise<void> {
    await nextTick()
    editorEl()?.focus()
  }

  defineExpose({ activate, reset, markActive, focus })
</script>

<style scoped>
.rc {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid var(--border-grey-color);
  border-radius: 14px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.rc--active,
.rc:focus-within {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--primary-light-color-transparent);
}
.rc__composer :deep(.mail-editor-toolbar) { border-radius: 14px 14px 0 0; }
.rc__composer :deep(.mail-editor) {
  min-height: 42px;
  max-height: 38vh;
  padding: 10px 14px;
  font-size: 0.875rem;
}
.rc--active .rc__composer :deep(.mail-editor) { min-height: 110px; }
.rc__composer :deep(.mail-editor p) { margin: 0 0 0.4em; }

.rc__files { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 12px 8px; }
.rc__file {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px 4px 9px;
  border-radius: 8px;
  background: var(--grey-light-color);
  font-size: 0.75rem;
}
.rc__file-x {
  display: inline-flex;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--grey-color);
  cursor: pointer;
}
.rc__file-x:hover { background: var(--grey-medium-color); }

.rc__tools {
  flex-shrink: 0;
  padding: 6px 8px 8px;
  border-top: 1px solid #eef0f3;
}
</style>
